// Amplify Data access layer for the 5 persisted core models.
// payments + quoteVisits remain local-only state for now (see App.jsx).
//
// Nested values are stored as JSON strings in the backend (see amplify/data/resource.ts),
// so each model has to<->from mappers that (de)serialize those fields. The rest of the app
// works with the rich in-memory shape and never sees the JSON strings.

import { generateClient } from "aws-amplify/data";

const client = generateClient();

// ---- field (de)serialization per model ----

const mappers = {
  Customer: {
    toApp: (r) => ({ id: r.id, name: r.name, phone: r.phone, email: r.email, address: r.address, notes: r.notes, clientStatus: r.clientStatus }),
    toBackend: (c) => ({ name: c.name, phone: c.phone, email: c.email, address: c.address, notes: c.notes, clientStatus: c.clientStatus }),
  },
  Job: {
    toApp: (r) => ({
      id: r.id,
      customerId: r.customerId,
      teamId: r.teamId,
      date: r.date,
      time: r.time,
      service: r.service,
      status: r.status,
      notes: r.notes,
      assignments: r.assignments ? JSON.parse(r.assignments) : [],
      recurrence: r.recurrence ? JSON.parse(r.recurrence) : { frequency: "none" },
      seriesId: r.seriesId ?? null,
    }),
    toBackend: (j) => ({
      customerId: j.customerId,
      teamId: j.teamId,
      date: j.date,
      time: j.time,
      service: j.service,
      status: j.status,
      notes: j.notes,
      assignments: JSON.stringify(j.assignments || []),
      recurrence: JSON.stringify(j.recurrence || { frequency: "none" }),
      seriesId: j.seriesId ?? null,
    }),
  },
  Team: {
    toApp: (r) => ({ id: r.id, name: r.name, color: r.color, members: r.members ? JSON.parse(r.members) : [] }),
    toBackend: (t) => ({ name: t.name, color: t.color, members: JSON.stringify(t.members || []) }),
  },
  Member: {
    toApp: (r) => ({ id: r.id, name: r.name, phone: r.phone, address: r.address, hireDate: r.hireDate, notes: r.notes }),
    toBackend: (m) => ({ name: m.name, phone: m.phone, address: m.address, hireDate: m.hireDate, notes: m.notes }),
  },
  Expense: {
    toApp: (r) => ({ id: r.id, date: r.date, category: r.category, description: r.description, amount: r.amount }),
    toBackend: (e) => ({ date: e.date, category: e.category, description: e.description, amount: Number(e.amount) }),
  },
  QuoteVisit: {
    toApp: (r) => ({ id: r.id, customerId: r.customerId, member: r.member, date: r.date, time: r.time, status: r.status, notes: r.notes }),
    toBackend: (v) => ({ customerId: v.customerId, member: v.member, date: v.date, time: v.time, status: v.status, notes: v.notes }),
  },
  Payment: {
    toApp: (r) => ({ id: r.id, customerId: r.customerId, jobId: r.jobId, date: r.date, amount: r.amount, method: r.method, notes: r.notes }),
    toBackend: (p) => ({ customerId: p.customerId, jobId: p.jobId, date: p.date, amount: Number(p.amount), method: p.method, notes: p.notes }),
  },
  Invoice: {
    toApp: (r) => ({
      id: r.id,
      invoiceNumber: r.invoiceNumber,
      customerId: r.customerId,
      issueDate: r.issueDate,
      periodStart: r.periodStart,
      periodEnd: r.periodEnd,
      lineItems: r.lineItems ? JSON.parse(r.lineItems) : [],
      notes: r.notes,
      total: r.total,
      status: r.status,
    }),
    toBackend: (i) => ({
      invoiceNumber: i.invoiceNumber,
      customerId: i.customerId,
      issueDate: i.issueDate,
      periodStart: i.periodStart,
      periodEnd: i.periodEnd,
      lineItems: JSON.stringify(i.lineItems || []),
      notes: i.notes,
      total: Number(i.total) || 0,
      status: i.status,
    }),
  },
};

async function listAll(model) {
  const { data, errors } = await client.models[model].list({ limit: 1000 });
  if (errors) throw new Error(`Failed to load ${model}: ${JSON.stringify(errors)}`);
  return data.map(mappers[model].toApp);
}

async function createOne(model, item) {
  const payload = mappers[model].toBackend(item);
  // Preserve client-supplied ids so cross-references (payments/quoteVisits -> customer/job,
  // jobs -> customer/team) stay valid instead of being remapped to server UUIDs.
  if (item.id) payload.id = item.id;
  const { data, errors } = await client.models[model].create(payload);
  if (errors) throw new Error(`Failed to create ${model}: ${JSON.stringify(errors)}`);
  return mappers[model].toApp(data);
}

async function updateOne(model, item) {
  const { data, errors } = await client.models[model].update({ id: item.id, ...mappers[model].toBackend(item) });
  if (errors) throw new Error(`Failed to update ${model}: ${JSON.stringify(errors)}`);
  return mappers[model].toApp(data);
}

async function deleteOne(model, id) {
  const { errors } = await client.models[model].delete({ id });
  if (errors) throw new Error(`Failed to delete ${model}: ${JSON.stringify(errors)}`);
}

// Loads every core collection in parallel.
export async function loadAll() {
  const [customers, jobs, teams, members, expenses, quoteVisits, payments, invoices] = await Promise.all([
    listAll("Customer"),
    listAll("Job"),
    listAll("Team"),
    listAll("Member"),
    listAll("Expense"),
    listAll("QuoteVisit"),
    listAll("Payment"),
    listAll("Invoice"),
  ]);
  return { customers, jobs, teams, members, expenses, quoteVisits, payments, invoices };
}

// Create that tolerates "already exists" (DynamoDB ConditionalCheckFailed), so seeding
// is idempotent and safe to re-run / run concurrently without erroring.
async function createIfAbsent(model, item) {
  try {
    return await createOne(model, item);
  } catch (err) {
    if (String(err).includes("ConditionalCheckFailed")) return null; // id already present
    throw err;
  }
}

// Seed the backend from the app's initial data, preserving seed ids. Only seeds
// collections that are currently empty (so it self-heals a partial seed and won't
// clobber existing records). Returns the freshly loaded collections.
export async function seedAll(seed, existing) {
  const tasks = [];
  if (existing.customers.length === 0) tasks.push(...seed.customers.map((c) => createIfAbsent("Customer", c)));
  if (existing.teams.length === 0) tasks.push(...seed.teams.map((t) => createIfAbsent("Team", t)));
  if (existing.members.length === 0) tasks.push(...seed.members.map((m) => createIfAbsent("Member", m)));
  if (existing.jobs.length === 0) tasks.push(...seed.jobs.map((j) => createIfAbsent("Job", j)));
  if (existing.expenses.length === 0) tasks.push(...seed.expenses.map((e) => createIfAbsent("Expense", e)));
  if (existing.quoteVisits.length === 0) tasks.push(...(seed.quoteVisits || []).map((v) => createIfAbsent("QuoteVisit", v)));
  if (existing.payments.length === 0) tasks.push(...(seed.payments || []).map((p) => createIfAbsent("Payment", p)));
  await Promise.all(tasks);
  return loadAll();
}

// Email an invoice to a client via the sendInvoiceEmail backend mutation (AWS SES).
// Returns { ok: boolean, messageId?: string, error?: string }.
export async function emailInvoice({ toEmail, invoice, customer, business }) {
  // AppSync's AWSJSON scalar expects a JSON string, not a raw object.
  const { data, errors } = await client.mutations.sendInvoiceEmail({
    toEmail,
    invoice: JSON.stringify(invoice),
    customer: JSON.stringify(customer),
    business: JSON.stringify(business),
  });
  if (errors?.length) {
    throw new Error(errors.map((e) => e.message).join("; "));
  }
  // The mutation returns AWSJSON; the client may hand it back as a string.
  const result = typeof data === "string" ? JSON.parse(data) : data;
  if (!result?.ok) {
    throw new Error(result?.error || "Failed to send email.");
  }
  return result;
}

// Per-model CRUD used by the App handlers.
export const db = {
  customer: {
    create: (c) => createOne("Customer", c),
    update: (c) => updateOne("Customer", c),
    remove: (id) => deleteOne("Customer", id),
  },
  job: {
    create: (j) => createOne("Job", j),
    update: (j) => updateOne("Job", j),
    remove: (id) => deleteOne("Job", id),
  },
  team: {
    create: (t) => createOne("Team", t),
    update: (t) => updateOne("Team", t),
    remove: (id) => deleteOne("Team", id),
  },
  member: {
    create: (m) => createOne("Member", m),
    update: (m) => updateOne("Member", m),
    remove: (id) => deleteOne("Member", id),
  },
  expense: {
    create: (e) => createOne("Expense", e),
    update: (e) => updateOne("Expense", e),
    remove: (id) => deleteOne("Expense", id),
  },
  quoteVisit: {
    create: (v) => createOne("QuoteVisit", v),
    update: (v) => updateOne("QuoteVisit", v),
    remove: (id) => deleteOne("QuoteVisit", id),
  },
  payment: {
    create: (p) => createOne("Payment", p),
    update: (p) => updateOne("Payment", p),
    remove: (id) => deleteOne("Payment", id),
  },
  invoice: {
    create: (i) => createOne("Invoice", i),
    update: (i) => updateOne("Invoice", i),
    remove: (id) => deleteOne("Invoice", id),
  },
};
